/** Police raids on a building — telegraphed, abstracted, consequential.
 *  Product is seized from the building; cash from the shared bankroll. */
import type { City, Building } from './types';
import { SIM } from './config';
import { Rng } from './rng';
import { totalDefense, safeThreshold } from './heat';
import { roomsOfType, pushLog, pushToast } from './world';
import { addThought, isAvailable } from './colonist';

function stashProtection(b: Building, now: number): number {
  return Math.min(0.6, roomsOfType(b, 'stash').filter((r) => r.damagedUntil <= now).length * 0.25);
}

function raidStrength(city: City, b: Building): number {
  const wealth = city.cash / 800 + b.rooms.length * 1.2;
  return SIM.RAID_BASE_STRENGTH + b.heat * 0.25 + wealth;
}

export function rollRaid(city: City, b: Building, dt: number): void {
  // Lying low suppresses new raids outright (not just a mid-telegraph discount) —
  // shutting down the operation for a bit is now a real way to buy safety.
  if (b.layingLow) return;

  b.raidTimer -= dt;
  if (b.raidTimer > 0) return;
  b.raidTimer = SIM.RAID_ROLL_SEC;
  if (b.raid) return;

  const safe = safeThreshold(b);
  if (b.heat <= safe) return;
  const chance = Math.min(1, (b.heat - safe) / (100 - safe)) * SIM.BASE_RAID_RATE;

  const rng = new Rng(city.rngSeed);
  const hit = rng.next() < chance;
  city.rngSeed = rng.seed;
  if (!hit) return;

  b.raid = {
    resolveAt: city.t + SIM.RAID_TELEGRAPH_SEC,
    strength: raidStrength(city, b),
    bribeCost: Math.max(120, Math.round(city.cash * 0.2 * (b.heat / 100))),
    bracedRoomId: null,
  };
  pushLog(city, 'raid', `🚨 Raid coming on ${b.name} — ${SIM.RAID_TELEGRAPH_SEC}s. Brace a room, lay low, or bribe.`);
  if (b.id !== city.activeId) {
    pushToast(city, b.id, 'raid', `🚨 Raid incoming at ${b.name}`, SIM.RAID_TELEGRAPH_SEC);
  }
}

export function resolveRaid(city: City, b: Building): 'none' | 'repelled' | 'hit' {
  const raid = b.raid;
  if (!raid || city.t < raid.resolveAt) return 'none';
  city.raidsFaced += 1;

  // computed while b.raid is still set, so a braced room's defense bonus applies
  const defense = totalDefense(b, city.t);
  const braced = raid.bracedRoomId != null ? b.rooms.find((r) => r.id === raid.bracedRoomId) : undefined;
  b.raid = null;
  const effective = b.layingLow ? raid.strength * 0.5 : raid.strength;
  const margin = effective - defense;
  const awayToast = b.id !== city.activeId;

  if (margin <= 0) {
    city.raidsSurvived += 1;
    b.heat = Math.min(b.heat, 22);
    pushLog(city, 'good', `🛡️ ${b.name} repelled the raid.`);
    if (awayToast) pushToast(city, b.id, 'good', `🛡️ ${b.name} repelled a raid while you were away`, 8);
    return 'repelled';
  }

  const severity = Math.max(0.12, Math.min(1, margin / raid.strength));
  // bracing a stash reinforces it further, on top of the passive stash pool
  const protect = Math.max(0.1, 1 - stashProtection(b, city.t) - (braced?.type === 'stash' ? 0.25 : 0));

  b.res.product *= 1 - SIM.SEIZE_PRODUCT_MAX * severity * protect;
  city.cash *= 1 - SIM.SEIZE_CASH_MAX * severity * protect;

  const rng = new Rng(city.rngSeed);
  // whoever's braced in gets shielded from being the raid's victim
  const present = b.colonists.filter((c) => isAvailable(c, city.t) && !(braced && c.assignedRoomId === braced.id));
  if (present.length > 0) {
    const victim = present[rng.int(present.length)];
    if (severity > 0.55 && rng.next() < severity) {
      // a severe enough bust can put someone away for good, not just a stint
      const gone = severity > 0.8 && rng.next() < 0.25;
      victim.arrestedReturn = gone ? -1 : city.t + SIM.ARREST_RECOVER_SEC;
      victim.activity = 'arrested';
      victim.assignedRoomId = null;
      city.arrested += 1;
      pushLog(city, 'bad', gone ? `⛓️ ${victim.name} got sent away for good at ${b.name}.` : `🚔 ${victim.name} got arrested at ${b.name}.`);
      if (!gone) addThought(victim, city.t, 'arrest', 'Arrested', -12);
    } else {
      victim.downUntil = city.t + SIM.INJURE_RECOVER_SEC;
      pushLog(city, 'bad', `🩹 ${victim.name} was hurt in the raid.`);
      addThought(victim, city.t, 'hurt', 'Hurt in the raid', -8);
    }
  }

  // a braced room can't be the one that gets trashed
  const damagePool = braced ? b.rooms.filter((r) => r.id !== braced.id) : b.rooms;
  if (damagePool.length > 0) {
    const room = damagePool[rng.int(damagePool.length)];
    room.damagedUntil = city.t + SIM.ROOM_DAMAGE_SEC;
  }

  city.rngSeed = rng.seed;
  b.heat = Math.min(b.heat, 25);
  pushLog(city, 'raid', `🚨 ${b.name} got raided. They took a cut and trashed a room.`);
  if (awayToast) pushToast(city, b.id, 'raid', `🚨 ${b.name} got raided while you were away`, 8);
  return 'hit';
}
